import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";
import { AdminProductsService } from "../../../pages/admin-pages/products/services/admin-products.service";
import { ProductCategoryResponse } from "../../../pages/admin-pages/products/models/product-models";
import { AdminProductsActions } from "../../actions/admin-products.actions";
import { FailState } from "../../models/fail-state";
import { ProductCategoriesState } from "../../models/mp-admin/product-categories-state";
import { AppState } from "../../reducers";
import { AdminProductsSelectors } from "../../selectors/admin-products.selectors";

@Injectable()
export class ProductCategoriesEffects {
	productCategories = createEffect(() =>
		this.actions.pipe(
			ofType(AdminProductsActions.productCategories.get),
			withLatestFrom(this.store.select(AdminProductsSelectors.productCategories)),
			switchMap(([action, state]) =>
				this.canUseStoreValue(state, action.familyId)
					? of(AdminProductsActions.productCategories.getSuccess({ state, familyId: action.familyId }))
					: of(AdminProductsActions.productCategories.getApi({ familyId: action.familyId }))
			)
		)
	);

	productCategoriesApi = createEffect(() =>
		this.actions.pipe(
			ofType(AdminProductsActions.productCategories.getApi),
			switchMap((action) =>
				this.service.getProductCategories(action.familyId, undefined, undefined).pipe(
					map((categories) => {
						// Build categories by family map for efficient filtering
						const categoriesByFamily: { [familyId: string]: ProductCategoryResponse[] } = {};
						categories.forEach((cat) => {
							if (!categoriesByFamily[cat.productFamilyId]) {
								categoriesByFamily[cat.productFamilyId] = [];
							}
							categoriesByFamily[cat.productFamilyId].push(cat);
						});

						return AdminProductsActions.productCategories.getSuccess({
							state: { categories, categoriesByFamily, loading: false, hasLoaded: true },
							familyId: action.familyId
						});
					}),
					catchError((error) =>
						of(AdminProductsActions.productCategories.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private service: AdminProductsService,
		private store: Store<AppState>
	) {}

	private canUseStoreValue(state: ProductCategoriesState, familyId?: string): boolean {
		if (
			state === undefined ||
			_.has(state, "error") ||
			state.loading ||
			state.hasLoaded !== true ||
			!_.has(state, "categories") ||
			state.categories === undefined
		) {
			return false;
		}

		// If requesting all categories, check if data has loaded
		if (!familyId) {
			return true;
		}

		// If requesting for specific family, check if we have the index built
		return (
			_.has(state, "categoriesByFamily") &&
			state.categoriesByFamily !== undefined &&
			_.has(state.categoriesByFamily, familyId)
		);
	}
}
